import {
  type FilterValue,
  type Filter,
} from "@shopify/hydrogen-react/storefront-api-types";

import { type DropdownOption } from "@react/components/Dropdown/Dropdown.data";
import {
  COLLECTION_FILTER_IDS,
  COLLECTION_PARAMS,
} from "@react/sections/Collection/CollectionFilters/constants";
import { currencySymbol } from "@root/tablescape/utilities/currency";
import { type SortOption } from "@tablescape/state";

import {
  extractTaxonomyValueGID,
  getColorNameFromTaxonomyGID,
  getTaxonomyGIDFromColorName,
} from "./taxonomyColorMapper";

const MAX_PRICE_FILTER_VALUE = 1_000_000;

const isCollectionParamKey = (key: string): key is COLLECTION_PARAMS => {
  return (Object.values(COLLECTION_PARAMS) as string[]).includes(key);
};

/**
 * Converts taxonomy GID to readable color name for URL params
 * Example: "gid://shopify/TaxonomyValue/1" -> "black"
 */
const normalizeTaxonomyGIDForURL = (gid: string): string => {
  if (!gid.startsWith("gid://shopify/TaxonomyValue/")) {
    return gid; // Return as-is if not a taxonomy GID
  }

  // Use static mapping for English color name
  const staticColorName = getColorNameFromTaxonomyGID(gid);
  if (staticColorName) {
    return staticColorName.toLowerCase();
  }

  // Return GID as-is if no color name found
  return gid;
};

/**
 * Converts readable color name back to full GID
 * Example: "black" -> "gid://shopify/TaxonomyValue/1"
 */
const denormalizeTaxonomyGIDFromURL = (readableId: string): string => {
  // If already a GID, return as-is
  if (readableId.startsWith("gid://shopify/TaxonomyValue/")) {
    return readableId;
  }

  // Convert English color name to GID using static mapping
  // getTaxonomyGIDFromColorName handles case-insensitive matching and static fallback
  const gid = getTaxonomyGIDFromColorName(readableId);
  return gid ?? readableId; // Return GID if found, otherwise return as-is
};

/**
 * Maps filter IDs to consistent query parameter keys.
 * This ensures query parameters are language-independent by using filter IDs
 * instead of localized labels.
 */
const getQueryParamKeyFromFilterId = (filterId: string): string => {
  // Map known filter IDs to consistent English query parameter keys
  if (filterId === COLLECTION_FILTER_IDS.CAPACITY)
    return COLLECTION_PARAMS.CAPACITY;
  if (filterId === COLLECTION_FILTER_IDS.COLOR) return COLLECTION_PARAMS.COLOR;
  if (filterId === COLLECTION_FILTER_IDS.DIAMETER)
    return COLLECTION_PARAMS.DIAMETER;
  if (filterId === COLLECTION_FILTER_IDS.MATERIAL)
    return COLLECTION_PARAMS.MATERIAL;
  if (filterId === COLLECTION_FILTER_IDS.PRICE) return COLLECTION_PARAMS.PRICE;
  if (filterId === COLLECTION_FILTER_IDS.SIZE) return COLLECTION_PARAMS.SIZE;

  // For unknown/custom filters, extract a consistent key from the ID
  // This handles any new filters automatically without hardcoded mappings
  const extracted = filterId.split(".").pop()?.toLowerCase();
  return extracted || filterId.replace(/[^a-zA-Z0-9-]/g, "-").toLowerCase();
};

export const isCollectionFilterId = (
  key: string
): key is COLLECTION_FILTER_IDS => {
  return (Object.values(COLLECTION_FILTER_IDS) as string[]).includes(key);
};

export const isSortOption = (option: DropdownOption): option is SortOption => {
  if (!option || typeof option.value !== "object" || option.value === null) {
    return false;
  }
  const value = option.value;
  return (
    "reverse" in value &&
    typeof value.reverse === "boolean" &&
    "sortKey" in value
  );
};

const parsePriceFilter = (queryValue: string): FilterValue | null => {
  const match = queryValue.match(/^(\d+(?:\.\d+)?)-(\d+(?:\.\d+)?)$/);
  if (!match) return null;

  const min = parseFloat(match[1]);
  const max = parseFloat(match[2]);

  if (
    isNaN(min) ||
    isNaN(max) ||
    min < 0 ||
    max < 0 ||
    max > MAX_PRICE_FILTER_VALUE ||
    min >= max
  )
    return null;

  return {
    id: COLLECTION_FILTER_IDS.PRICE,
    count: 0,
    input: JSON.stringify({ price: { min, max } }),
    label: `${currencySymbol()}${min} - ${currencySymbol()}${max}`,
  };
};

export const getAppliedFiltersFromQuery = (
  availableFilters: Filter[] | undefined
): FilterValue[] => {
  if (!availableFilters || availableFilters.length === 0) return [];

  if (typeof window === "undefined") return [];
  const params = new URLSearchParams(window.location.search);
  const preservedFilters: FilterValue[] = [];

  for (const [queryKey, queryValue] of params.entries()) {
    if (queryKey === COLLECTION_PARAMS.PRICE) {
      const priceFilter = parsePriceFilter(queryValue);
      if (priceFilter) preservedFilters.push(priceFilter);
      continue;
    }

    const filterMatch = findFilterMatchByQueryKey(availableFilters, queryKey);
    if (!filterMatch) continue;

    // Check if this is a color filter using taxonomy IDs
    const isColorFilter = filterMatch.id === COLLECTION_FILTER_IDS.COLOR;

    // Build Map for O(1) lookups (performance optimization)
    // For color filters: key by normalized taxonomy GID (e.g., "TaxonomyValue1")
    // For non-color filters: key by label (lowercase)
    const valueMap = isColorFilter
      ? new Map(
          filterMatch.values
            .map((v) => {
              const gid = extractTaxonomyValueGID(v);
              const key = gid ? normalizeTaxonomyGIDForURL(gid) : null;
              return key ? [key, v] : null;
            })
            .filter((entry): entry is [string, FilterValue] => entry !== null)
        )
      : new Map(filterMatch.values.map((v) => [v.label.toLowerCase(), v]));

    const matchedValues = queryValue
      .split(",")
      .map((val) => {
        try {
          const decodedVal = decodeURIComponent(val.trim());

          if (isColorFilter) {
            // Convert readable color name (e.g., "black") to GID, then normalize for map lookup
            const fullGID = denormalizeTaxonomyGIDFromURL(decodedVal);
            const normalizedKey = normalizeTaxonomyGIDForURL(fullGID);
            return valueMap.get(normalizedKey);
          }

          // For non-color filters, match by label (e.g., material, size filters)
          return valueMap.get(decodedVal.toLowerCase());
        } catch {
          return undefined;
        }
      })
      .filter((v): v is FilterValue => v !== undefined);

    preservedFilters.push(...matchedValues);
  }

  return preservedFilters;
};

export const getAppliedSortFromQuery = (availableSorts: SortOption[]) => {
  if (availableSorts.length === 0) return undefined;

  const search = typeof window !== "undefined" ? window.location.search : "";
  const params = new URLSearchParams(search);
  const sortParam = params.get("sort");
  if (!sortParam) return undefined;

  return availableSorts.find(({ name }) => name === sortParam);
};

// Build only the filter params from state
const buildFilterParamEntries = (
  appliedFilters: FilterValue[],
  availableFilters: Filter[] | undefined
): Array<[string, string]> => {
  if (!availableFilters || appliedFilters.length === 0) return [];

  const grouped: Record<string, FilterValue[]> = {};
  for (const filterValue of appliedFilters) {
    const filterMatch = findFilterMatchByFilterValueObject(
      availableFilters,
      filterValue
    );
    if (!filterMatch) continue;

    const key = getQueryParamKeyFromFilterId(filterMatch.id);
    (grouped[key] ||= []).push(filterValue);
  }

  const entries: Array<[string, string]> = [];
  for (const [key, values] of Object.entries(grouped)) {
    let valueString: string;

    if (key === COLLECTION_PARAMS.PRICE) {
      valueString = generatePriceParamValueString(values);
    } else {
      // Find the filter ID for this key to determine if it's a color filter
      const filterMatch = availableFilters?.find(
        (f) => getQueryParamKeyFromFilterId(f.id) === key
      );
      valueString = generateParamValueString(values, filterMatch?.id);
    }

    if (valueString) entries.push([key, valueString]);
  }

  return entries;
};

// Build only the sort param from state
export const buildSortParam = (
  appliedSort: SortOption | undefined,
  availableSorts: SortOption[]
): string | undefined => {
  if (!appliedSort || availableSorts.length === 0) return undefined;
  const match = availableSorts.find(({ name }) => name === appliedSort.name);

  // Only add sort parameter if it's not the default/first sort option
  if (match && availableSorts.length > 0) {
    const isDefaultSort = match.name === availableSorts[0].name;
    if (isDefaultSort) {
      return;
    }
  }

  return match ? match.name : undefined;
};

// Compose a new search string from existing search + filter and sort params (pure)
export const buildSearchFromFilterSortState = ({
  appliedFilters,
  appliedSort,
  availableFilters,
  availableSorts,
  existingSearch,
}: {
  appliedFilters: FilterValue[];
  appliedSort: SortOption | undefined;
  availableFilters: Filter[] | undefined;
  availableSorts: SortOption[];
  existingSearch: string;
}): string => {
  const params = new URLSearchParams(existingSearch);

  // Clear all filter/sort params
  for (const key of Array.from(params.keys())) {
    if (isCollectionParamKey(key)) {
      params.delete(key);
    }
  }

  // Set filter params
  const filterEntries = buildFilterParamEntries(
    appliedFilters,
    availableFilters
  );
  for (const [key, value] of filterEntries) {
    params.set(key, value);
  }

  // Set sort param
  const sortName = buildSortParam(appliedSort, availableSorts);
  if (sortName) {
    params.set("sort", sortName);
  }

  return params.toString();
};

export const updateQueryFromFilterSortState = ({
  appliedFilters,
  appliedSort,
  availableFilters,
  availableSorts,
}: {
  appliedFilters: FilterValue[];
  appliedSort: SortOption | undefined;
  availableFilters: Filter[] | undefined;
  availableSorts: SortOption[];
}) => {
  if (typeof window === "undefined") return;
  const existingSearch = window.location.search;

  const newSearch = buildSearchFromFilterSortState({
    appliedFilters,
    appliedSort,
    availableFilters,
    availableSorts,
    existingSearch,
  });

  const pathname = window.location.pathname;
  const newUrl = `${pathname}${newSearch ? `?${newSearch}` : ""}`;
  if (window.history?.replaceState) {
    window.history.replaceState(null, "", newUrl);
  }
};

// For the queryfilter params during URL→state initialization
// Finds filter by ID-based mapping (e.g., "color" - consistent across languages)
const findFilterMatchByQueryKey = (
  availableFilters: Filter[],
  queryKey: string
) => {
  return availableFilters.find(
    (f) => getQueryParamKeyFromFilterId(f.id) === queryKey
  );
};

// Helper to find the parent filter from an applied FilterValue during
// state→query updates.
// Example FilterValue:
// { id: "filter.v.m.collection.name__Char", count: 0, input: "Char", label: "Char" }
const findFilterMatchByFilterValueObject = (
  availableFilters: Filter[],
  filterValue: FilterValue
) => {
  return availableFilters.find((f) =>
    f.values.find((v) => filterValue.id.includes(v.id))
  );
};

const generateParamValueString = (
  filterValues: FilterValue[],
  filterId: string | undefined
): string => {
  // For color filters, use readable color names instead of labels
  const isColorFilter = filterId === COLLECTION_FILTER_IDS.COLOR;

  if (isColorFilter) {
    return filterValues
      .map((v) => {
        const gid = extractTaxonomyValueGID(v);
        return gid ? normalizeTaxonomyGIDForURL(gid) : v.label.toLowerCase();
      })
      .join(",");
  }

  // For other filters, use labels (backward compatibility)
  return filterValues.map((v) => v.label.toLowerCase()).join(",");
};

const generatePriceParamValueString = (filterValues: FilterValue[]): string => {
  const input = filterValues[0]?.input;
  if (!input || typeof input !== "string") return "";

  try {
    const { price } = JSON.parse(input);
    const { max, min } = price;
    if (typeof min === "number" && typeof max === "number" && min < max) {
      return `${min}-${max}`;
    }
  } catch (error) {
    console.warn("Invalid price query value:", input, error);
  }
  return "";
};

export const createDefaultCollectionSortOptions = (labels: {
  bestsellersLabel: string;
  featuredLabel: string;
  newestLabel: string;
  priceHighToLowLabel: string;
  priceLowToHighLabel: string;
}): SortOption[] => [
  {
    value: { sortKey: "COLLECTION_DEFAULT", reverse: false },
    name: labels.featuredLabel,
  },
  {
    value: { sortKey: "CREATED", reverse: true },
    name: labels.newestLabel,
  },
  {
    value: { sortKey: "PRICE", reverse: false },
    name: labels.priceLowToHighLabel,
  },
  {
    value: { sortKey: "PRICE", reverse: true },
    name: labels.priceHighToLowLabel,
  },
  {
    value: { sortKey: "BEST_SELLING", reverse: false },
    name: labels.bestsellersLabel,
  },
];
